import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, BarChart3 } from 'lucide-react';

import { useSeasonsQuery } from '@/services/endpoints/leaderboard';
import { useGameSelection } from '@/hooks/useGameSelection';
import { Loader } from '@/components/common/Loader';
import { cdnImage } from '@/lib/cdnImage';
import { cn } from '@/lib/cn';
import { env } from '@/env';

export function Leaderboards() {
  const { t } = useTranslation('leaderboard');
  const { activeGame, games: allGames } = useGameSelection();
  const games = activeGame ? String(activeGame.id) : undefined;

  const { data, isLoading, isError } = useSeasonsQuery({
    games,
    is_active: true,
  });

  const seasons = data?.data ?? [];

  return (
    <div className="flex flex-col gap-4 px-4 pb-6 pt-5">
      <header>
        <h1 className="text-2xl font-bold text-fg">{t('title')}</h1>
        <p className="mt-1 text-sm text-fg-muted">{t('subtitle')}</p>
      </header>

      {isLoading && (
        <div className="flex h-40 items-center justify-center">
          <Loader />
        </div>
      )}

      {isError && (
        <div className="rounded-xl bg-bg-secondary p-6 text-center text-sm text-danger">
          {t('load_error')}
        </div>
      )}

      {!isLoading && !isError && seasons.length === 0 && (
        <div className="rounded-xl bg-bg-secondary p-6 text-center text-sm text-fg-muted">
          {t('empty')}
        </div>
      )}

      <ul className="flex flex-col gap-2">
        {seasons.map((season) => (
          <li key={season.id}>
            <Link
              to={`/leaderboards/${season.id}`}
              className={cn(
                'flex items-center gap-3 rounded-2xl bg-bg-secondary p-3',
                'transition-opacity active:opacity-80',
              )}
            >
              <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-bg-tertiary">
                {season.image ? (
                  <img
                    src={cdnImage(season.image)}
                    alt=""
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <BarChart3 aria-hidden className="h-6 w-6 text-brand" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                {!env.featureCrOnly &&
                  (() => {
                    const gameNameById = new Map(allGames.map((g) => [g.id, g.name]));
                    const gameName = gameNameById.get(season.game_id);
                    return gameName ? (
                      <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-brand">
                        {gameName}
                      </p>
                    ) : null;
                  })()}
                <p className="truncate text-sm font-bold text-fg">{season.title}</p>
                {season.description && (
                  <p className="line-clamp-1 text-xs text-fg-muted">{season.description}</p>
                )}
              </div>
              <ChevronRight aria-hidden className="h-5 w-5 text-fg-muted" />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
